import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { 
  Check, Users, Building2, Car, DoorOpen, Zap, 
  CreditCard, Bell, MessageSquare, BarChart3, FileText,
  Sparkles, ArrowRight, TrendingUp, Calendar, Shield
} from 'lucide-react';

function CoworksPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const heroFeatures = [
    "Gestión 360° de Membresías",
    "Facturación Automática",
    "Control de Acceso Integrado",
    "App Personalizada"
  ];

  const coreModules = [
    {
      icon: TrendingUp,
      title: "Pipeline de Ventas Completo",
      description: "Gestiona leads, potenciales y embudo de ventas. Convierte visitantes en miembros con cotizaciones automáticas.",
      modules: ["Leads", "Potenciales", "Embudo de venta", "Cotizaciones"]
    },
    {
      icon: Users,
      title: "Gestión de Miembros",
      description: "Base de datos centralizada de todos tus miembros con historial completo y seguimiento de satisfacción.",
      modules: ["Gestión de Clientes", "Satisfacción NPS", "WhatsApp Business"]
    },
    {
      icon: Building2,
      title: "Espacios & Amenities",
      description: "Administra salas de reunión, escritorios, espacios comunes y estacionamientos desde un solo lugar.",
      modules: ["Gestión de espacios", "Gestión de parking", "Gestión de Visitas"]
    },
    {
      icon: Shield,
      title: "Control de Acceso Inteligente",
      description: "Acceso con QR, credencial o app móvil. Gestión de visitantes y correspondencia integrada.",
      modules: ["Control de acceso", "Gestión de Visitas", "Gestión de correspondencia"]
    },
    {
      icon: CreditCard,
      title: "Facturación Automática",
      description: "Facturación electrónica recurrente con notificaciones y suspensión automática por mora.",
      modules: ["Facturación electrónica", "Facturación recurrente", "Gestión de suscripciones"]
    },
    {
      icon: MessageSquare,
      title: "Soporte & Comunicación",
      description: "Sistema de tickets, WhatsApp Business y mailing masivo para mantener comunicación fluida.",
      modules: ["Ticket de soporte", "WhatsApp Business", "Mailing Masivo"]
    }
  ];

  const benefits = [
    {
      title: "Automatiza la Renovación",
      description: "Facturación recurrente con notificaciones automáticas y suspensión por mora sin intervención manual.",
      icon: Zap
    },
    {
      title: "Maximiza Ocupación",
      description: "Pipeline de ventas optimizado que convierte leads en miembros activos más rápido.",
      icon: TrendingUp
    },
    {
      title: "Reduce Operaciones",
      description: "Gestión de acceso, espacios y cobranza 100% automatizada. Ahorra hasta 40% de tiempo operativo.",
      icon: BarChart3
    },
    {
      title: "Experiencia Premium",
      description: "App personalizada, acceso sin contacto y comunicación fluida para una experiencia de clase mundial.",
      icon: Sparkles
    }
  ];

  const includedModules = [
    "✅ Leads & Pipeline de Ventas",
    "✅ Gestión de Miembros 360°",
    "✅ Cotizaciones Automáticas",
    "✅ Contratos & Firma Digital",
    "✅ Control de Acceso Integrado",
    "✅ Gestión de Espacios & Parking",
    "✅ Facturación Electrónica Recurrente",
    "✅ WhatsApp Business & Soporte",
    "✅ Gestión de Visitas & Correspondencia",
    "✅ App Móvil Personalizada",
    "✅ Mailing Masivo & Comunicación",
    "✅ NPS & Satisfacción de Clientes"
  ];

  return (
    <>
      <SEOHead
        title="Cloudbase para Coworks - Gestión Integral de Espacios Colaborativos"
        description="Plataforma todo-en-uno para coworks: gestión de miembros, facturación recurrente, control de acceso, espacios y más."
        keywords="coworking software, gestión coworks, facturación coworking, control acceso cowork"
      />
      <div className="min-h-screen bg-white">
        <Header />

        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-black relative overflow-hidden min-h-screen flex items-center">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black"></div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-5xl mx-auto text-center">
              <div className="inline-block mb-6 px-4 py-2 bg-green-600/20 border border-green-600/30 text-green-400 rounded-full text-sm font-semibold">
                Solución para Coworks
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-8 leading-tight text-white">
                Todo lo que necesitas para gestionar tu Cowork
              </h1>
              
              <p className="text-xl sm:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed font-light">
                Desde la captación del lead hasta la facturación recurrente. Una plataforma integral diseñada específicamente para espacios de coworking.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
                <Button 
                  size="lg" 
                  className="bg-green-600 hover:bg-green-700 text-white px-10 py-7 text-base rounded font-semibold transition-all duration-200"
                  onClick={() => window.location.href = '/demo'}
                >
                  Ver Demo Personalizada
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-black px-10 py-7 text-base rounded font-semibold transition-all duration-200"
                  onClick={() => window.location.href = '/demo'}
                >
                  Hablar con un Experto
                </Button>
              </div>

              {/* Trust indicators */}
              <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-400">
                {heroFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Core Modules Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Pack Completo para Coworks
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Todos los módulos que necesitas, integrados en una sola plataforma
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {coreModules.map((module, index) => {
                const Icon = module.icon;
                return (
                  <div
                    key={index}
                    className="p-8 bg-gray-50 rounded-lg hover:shadow-lg transition-shadow"
                  >
                    <Icon className="w-12 h-12 text-green-600 mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {module.title}
                    </h3>
                    <p className="text-gray-600 mb-4">{module.description}</p>
                    <div className="space-y-2">
                      {module.modules.map((mod, idx) => (
                        <div key={idx} className="text-sm text-green-600 font-medium">
                          • {mod}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Beneficios Reales para tu Cowork
              </h2>
              <p className="text-xl text-gray-600">
                Resultados que verás desde el primer mes
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div key={index} className="text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                      <Icon className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Modules Included */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Todo Incluido en tu Plan
              </h2>
              <p className="text-xl text-gray-600">
                Sin costos ocultos, sin módulos adicionales
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {includedModules.map((module, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg"
                >
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0" />
                  <span className="text-gray-900 font-medium">{module}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-green-600 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              ¿Listo para transformar tu Cowork?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Agenda una demo personalizada y descubre cómo Cloudbase puede ayudarte a crecer.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-white text-green-600 hover:bg-gray-100 px-10 py-7 text-base rounded font-semibold transition-all duration-200 inline-flex items-center gap-2"
                onClick={() => window.location.href = '/demo'}
              >
                Agendar Demo Gratuita
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
            <p className="mt-8 text-white/70 text-sm">
              Implementación en 48 horas • Soporte dedicado • Sin contratos a largo plazo
            </p>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}

export default CoworksPage;